import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = ['content', 'toggleButton', 'toggleButtonLabel'];
  static values = {
    maxLines: { type: Number, default: 3 },
    moreLabel: { type: String, default: 'Show more' },
    lessLabel: { type: String, default: 'Show less' },
    collapsedLayoutClass: { type: String, default: '' },
    expandedLayoutClass: { type: String, default: '' },
  };

  connect() {
    this.collapse();
    this.hideButtonIfNotNeeded();
    this.resizeObserver = new ResizeObserver(() => this.hideButtonIfNotNeeded());
    this.resizeObserver.observe(this.contentTarget);
  }

  disconnect() {
    this.resizeObserver?.disconnect();
  }

  collapse() {
    const content = this.contentTarget;
    content.classList.remove('line-clamp-none');
    content.classList.add(`line-clamp-${this.maxLinesValue}`);
    if (this.hasToggleButtonLabelTarget) {
      this.toggleButtonLabelTarget.textContent = this.moreLabelValue;
    }
    this.applyLayout(false);
  }

  hideButtonIfNotNeeded() {
    if (!this.hasToggleButtonTarget) return;
    this.toggleButtonTarget.classList.toggle('hidden', !this.overflowsWhenCollapsed());
  }

  // Expanded content never overflows, so the clamp is applied for the measurement only and
  // restored in the same frame. Otherwise widening the screen after "Show more" leaves the
  // button visible with nothing left to truncate.
  overflowsWhenCollapsed() {
    const content = this.contentTarget;
    const lineClampClass = `line-clamp-${this.maxLinesValue}`;
    const isExpanded = content.classList.contains('line-clamp-none');

    if (isExpanded) content.classList.replace('line-clamp-none', lineClampClass);
    const isOverflowing = content.scrollHeight - content.clientHeight > this.overflowTolerance();
    if (isExpanded) content.classList.replace(lineClampClass, 'line-clamp-none');

    return isOverflowing;
  }

  // At fractional zoom levels (e.g. 90%) fonts taller than their line box make scrollHeight
  // round 1px above clientHeight with nothing hidden. A truncated line is always a full line
  // high, so anything under half a line is rounding, not overflow.
  overflowTolerance() {
    const lineHeight = parseFloat(getComputedStyle(this.contentTarget).lineHeight);
    return Number.isNaN(lineHeight) ? 1 : lineHeight / 2;
  }

  toggle() {
    const content = this.contentTarget;
    const isExpanded = content.classList.contains('line-clamp-none');
    const lineClampClass = `line-clamp-${this.maxLinesValue}`;

    if (isExpanded) {
      content.classList.remove('line-clamp-none');
      content.classList.add(lineClampClass);
      this.toggleButtonLabelTarget.textContent = this.moreLabelValue;
      this.applyLayout(false);
    } else {
      content.classList.remove(lineClampClass);
      content.classList.add('line-clamp-none');
      this.toggleButtonLabelTarget.textContent = this.lessLabelValue;
      this.applyLayout(true);
    }
  }

  applyLayout(expanded) {
    if (!this.collapsedLayoutClassValue || !this.expandedLayoutClassValue) return;

    const collapsedClasses = this.collapsedLayoutClassValue.split(' ').filter(Boolean);
    const expandedClasses = this.expandedLayoutClassValue.split(' ').filter(Boolean);
    const [classesToAdd, classesToRemove] = expanded
      ? [expandedClasses, collapsedClasses]
      : [collapsedClasses, expandedClasses];

    this.element.classList.remove(...classesToRemove);
    this.element.classList.add(...classesToAdd);
  }
}
